import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import CustomModal from "../../../components/modal/CustomModal";
import { bibleApi } from "../bible-api";
import {
  Occasion,
  BibleVersion,
  BibleBook,
  BibleChapter,
  BibleVerse,
  Language,
  CreateOccasionVerseDto,
} from "../types";

interface AddOccasionVerseModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  occasions: Occasion[];
}

export default function AddOccasionVerseModal({
  open,
  onClose,
  onSuccess,
  occasions,
}: AddOccasionVerseModalProps) {
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState<BibleVersion[]>([]);
  const [books, setBooks] = useState<BibleBook[]>([]);
  const [chapters, setChapters] = useState<BibleChapter[]>([]);
  const [verses, setVerses] = useState<BibleVerse[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [selectedBookData, setSelectedBookData] = useState<BibleBook | null>(null);

  const [selectedOccasion, setSelectedOccasion] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [selectedBook, setSelectedBook] = useState<number | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [selectedVerse, setSelectedVerse] = useState<number | null>(null);
  const [overrideText, setOverrideText] = useState<string>("");
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [enableOverwrite, setEnableOverwrite] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      fetchInitialData();
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (selectedBook) {
      fetchChapters(selectedBook);
    } else {
      setChapters([]);
      setVerses([]);
      setSelectedChapter(null);
      setSelectedVerse(null);
    }
  }, [selectedBook]);

  useEffect(() => {
    if (selectedBook && selectedChapter && selectedVersion) {
      fetchVerses();
    } else {
      setVerses([]);
      setSelectedVerse(null);
    }
  }, [selectedBook, selectedChapter, selectedVersion]);

  useEffect(() => {
    if (selectedVerse && verses.length > 0) {
      const verse = verses.find((v) => v.verse_index === selectedVerse);
      if (verse && enableOverwrite) {
        setOverrideText(verse.text);
      } else if (!enableOverwrite) {
        setOverrideText("");
      }
    }
  }, [selectedVerse, verses, enableOverwrite]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [versionsData, booksData, languagesData] = await Promise.all([
        bibleApi.getAllVersions(),
        bibleApi.getAllBooks(),
        bibleApi.getLanguages(),
      ]);
      setVersions(versionsData);
      setBooks(booksData || []);
      setLanguages(languagesData);
    } catch (error: any) {
      toast.error("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async (bookId: number) => {
    try {
      const chaptersData = await bibleApi.getChaptersByBook(bookId);
      setChapters(chaptersData);
      setSelectedChapter(null);
      setSelectedVerse(null);
      setVerses([]);
    } catch (error: any) {
      toast.error("Failed to load chapters");
    }
  };

  const fetchVerses = async () => {
    if (!selectedBook || !selectedChapter || !selectedVersion) return;

    setLoading(true);
    try {
      const versesData = await bibleApi.getVerses({
        versionIds: [selectedVersion],
        bookId: selectedBook,
        chapterNumber: selectedChapter,
      });
      
      if (versesData[selectedVersion]?.chapters[selectedChapter]) {
        setVerses(versesData[selectedVersion].chapters[selectedChapter]);
      } else {
        setVerses([]);
      }
    } catch (error: any) {
      toast.error("Failed to load verses");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedOccasion(null);
    setSelectedVersion(null);
    setSelectedBook(null);
    setSelectedChapter(null);
    setSelectedVerse(null);
    setOverrideText("");
    setSelectedLanguage("");
    setSelectedBookData(null);
    setEnableOverwrite(false);
  };

  const handleSubmit = async () => {
    if (!selectedOccasion) {
      toast.warning("Please select an occasion");
      return;
    }

    if (!selectedVersion || !selectedBook || !selectedChapter || !selectedVerse) {
      toast.warning("Please select version, book, chapter, and verse");
      return;
    }

    if (enableOverwrite && !overrideText.trim()) {
      toast.warning("Please enter verse text when overwrite is enabled");
      return;
    }

    setLoading(true);
    try {
      const data: CreateOccasionVerseDto = {
        occasion_index: selectedOccasion,
        version_index: selectedVersion,
        book_index: selectedBook,
        chapter_index: selectedChapter,
        verse_index: selectedVerse,
        override_text: enableOverwrite ? overrideText.trim() : undefined,
        override_language_code: enableOverwrite ? (selectedLanguage || undefined) : undefined,
      };

      await bibleApi.createOccasionVerse(data);
      toast.success("Verse added to occasion successfully!");
      resetForm();
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to add verse to occasion");
    } finally {
      setLoading(false);
    }
  };

  const selectedBookName = books.find((b) => b.index === selectedBook)?.name || "";

  return (
    <CustomModal open={open} onClose={onClose} className="!w-[75vw] !max-w-[75vw] min-h-[50vh]">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Add Verse to Occasion
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <i className="fa-solid fa-times text-xl"></i>
          </button>
        </div>

        {loading && !versions.length ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Horizontal Dropdowns Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Occasion Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Occasion <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedOccasion || ""}
                  onChange={(e) => setSelectedOccasion(e.target.value as string)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
                >
                  <option value="">Select Occasion</option>
                  {occasions.map((occasion) => (
                    <option key={occasion.id} value={occasion.index}>
                      {occasion.name} {occasion.type && `(${occasion.type})`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Bible Version Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bible Version <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedVersion || ""}
                  onChange={(e) => {
                    setSelectedVersion(e.target.value ? Number(e.target.value) : null);
                    setSelectedBook(null);
                    setSelectedChapter(null);
                    setSelectedVerse(null);
                  }}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
                >
                  <option value="">Select Version</option>
                  {versions.map((version) => (
                    <option key={version.id} value={version.id}>
                      {version.name} {version.abbreviation && `(${version.abbreviation})`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Book Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Book <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedBook || ""}
                  onChange={(e) => {
                    const bookIndex = e.target.value ? Number(e.target.value) : null;
                    setSelectedBook(bookIndex);
                    if (bookIndex !== null) {
                      const book = books.find((b) => b.index === bookIndex);
                      setSelectedBookData(book || null);
                    } else {
                      setSelectedBookData(null);
                    }
                  }}
                  disabled={!selectedVersion || books.length === 0}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select Book</option>
                  {books.map((book) => (
                    <option key={book.id} value={book.index}>
                      {book.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Chapter Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Chapter <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedChapter || ""}
                  onChange={(e) => setSelectedChapter(e.target.value ? Number(e.target.value) : null)}
                  disabled={!selectedBook}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select Chapter</option>
                  {selectedBookData?.chapters.map((chapter) => (
                    <option key={chapter.chapter_index} value={chapter.chapter_index}>
                      Chapter {chapter.chapter_index}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Selected Verse Reference */}
            {selectedVerse && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3">
                <p className="text-lg font-semibold text-blue-700 dark:text-blue-300">
                  {selectedBookName} {selectedChapter}:{selectedVerse}
                </p>
              </div>
            )}

            {/* Verses List */}
            {verses.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Verse <span className="text-red-500">*</span>
                </label>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full">
                    <tbody>
                      {verses.map((verse) => (
                        <tr
                          key={verse.verse_index}
                          onClick={() => setSelectedVerse(verse.verse_index)}
                          className={`cursor-pointer transition-colors ${
                            selectedVerse === verse.verse_index
                              ? "bg-blue-100 dark:bg-blue-900/30"
                              : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                          } border-b border-gray-200 dark:border-gray-700`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <input
                                type="radio"
                                checked={selectedVerse === verse.verse_index}
                                onChange={() => setSelectedVerse(verse.verse_index)}
                                className="text-blue-600 focus:ring-blue-500"
                              />
                              <span className="font-semibold text-gray-700 dark:text-gray-300">
                                {verse.verse_index}
                              </span>
                              <span className="text-gray-600 dark:text-gray-400 flex-1">
                                {verse.text}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Overwrite Checkbox and Override Text with Language */}
            {selectedVerse && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="enableOverwrite"
                    checked={enableOverwrite}
                    onChange={(e) => {
                      setEnableOverwrite(e.target.checked);
                      if (!e.target.checked) {
                        setOverrideText("");
                        setSelectedLanguage("");
                      } else {
                        const verse = verses.find((v) => v.verse_index === selectedVerse);
                        if (verse) {
                          setOverrideText(verse.text);
                        }
                      }
                    }}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="enableOverwrite" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                    Overwrite verse text
                  </label>
                </div>

                {enableOverwrite && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Language for Override Text
                      </label>
                      <select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
                      >
                        <option value="">Select Language (Optional)</option>
                        {languages.map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Verse Text (Override) <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={overrideText}
                        onChange={(e) => setOverrideText(e.target.value)}
                        rows={6}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter or modify the verse text..."
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !selectedOccasion || !selectedVerse || (enableOverwrite && !overrideText.trim())}
                className="btn-primary px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-plus mr-2"></i>
                    Add Verse
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
}

